import { buildAuditHistoryJsonUrl, escapeHtml, formatAuditTimestamp } from './form_audit_history_module.js'

const INITIAL_SIGNATURE_MEANING =
  'I have reviewed the information contained in this record and attest that it is complete, accurate, and reflective of my professional assessment. By applying my electronic signature, I accept responsibility for this entry.'

const EDITED_SIGNATURE_MEANING =
  'I have reviewed the revised information contained in this record and attest that the changes are complete, accurate, and reflective of my professional assessment. By applying my electronic signature, I acknowledge and approve these modifications and accept responsibility for the updated record.'

function isInitialSubmission (version) {
  if (version.initial_submission != null) return version.initial_submission
  return ['Request Submitted', 'Initial Submission'].includes(version.action)
}

function buildSignatureHtml (version, { compact = true } = {}) {
  const maxHeight = compact ? '48px' : '64px'

  if (version.signature_url) {
    return `
      <div class="flex items-center">
        <img src="${version.signature_url}" alt="Electronic Signature" class="audit-history-signature" style="max-height: ${maxHeight}; width: auto; height: auto; display: block;">
      </div>
    `
  }

  return '<span class="text-xs text-gray-500 italic">No signature on file</span>'
}

function renderCertificatePanel (details, { hidden = true } = {}) {
  if (!details) return ''

  const sections = Array.isArray(details.sections) ? details.sections : []
  const hiddenClass = hidden ? 'hidden' : ''

  const sectionsHtml = sections.map((section) => {
    const lines = (section.lines || [])
      .map(([label, value]) => `
        <div class="text-xs text-gray-700" style="line-height: 1.45; margin-bottom: 1px;">
          <span class="font-semibold">${escapeHtml(label)}:</span> ${escapeHtml(value || '—')}
        </div>
      `)
      .join('')

    return `
      <div class="mb-2">
        <div class="text-xs font-bold uppercase tracking-wide text-gray-500 mb-1">${escapeHtml(section.title || 'Details')}</div>
        ${lines}
      </div>
    `
  }).join('')

  return `
    <div class="audit-history-certificate-panel ${hiddenClass} mt-2 p-2 bg-blue-50 border border-blue-100 rounded">
      <div class="text-xs font-bold text-blue-800 mb-2">Signature Certificate Details</div>
      ${sectionsHtml}
    </div>
  `
}

function renderUniqueId (uniqueId, certificateDetails) {
  const idValue = escapeHtml(uniqueId)
  const certificateHtml = certificateDetails ? renderCertificatePanel(certificateDetails, { hidden: true }) : ''

  return `
    <div class="mt-2 pt-2 border-t border-gray-200 text-xs text-gray-500" style="word-break: break-all;">
      <span class="font-semibold">Unique ID:</span>
      <button
        type="button"
        class="audit-history-unique-id-toggle text-blue-700 hover:text-blue-900 focus:outline-none"
        style="font-family: 'Courier New', Courier, monospace; letter-spacing: 0.03em; text-decoration: underline; text-decoration-style: dotted;"
        aria-expanded="false"
        title="Show signature certificate details"
      >
        ${idValue}
      </button>
      <span class="text-gray-400 italic ml-1">(click for details)</span>
      ${certificateHtml}
    </div>
  `
}

function renderDetailsList (details) {
  if (!Array.isArray(details) || !details.length) return ''

  const rows = details.map((detail) => `
    <div class="text-xs text-gray-700 mb-1">
      <span class="font-semibold">${escapeHtml(detail.label || 'Detail')}:</span>
      <span class="whitespace-pre-wrap">${escapeHtml(detail.value || '—')}</span>
    </div>
  `).join('')

  return `
    <div class="mt-2 pt-2 border-t border-gray-200">
      ${rows}
    </div>
  `
}

function buildVersionMeta (version) {
  const isInitial = isInitialSubmission(version)
  const actorLabel = isInitial ? 'Submitted By' : 'Recorded By'

  return {
    isInitial,
    title: version.action || (isInitial ? 'Request Submitted' : 'Response Recorded'),
    versionLabel: isInitial ? 'Initial' : `V${version.version_number}`,
    action: version.action || (isInitial ? 'Request Submitted' : 'Response Recorded'),
    actorLabel,
    actorName: version.user_name || '—',
    role: version.user_role || '—',
    department: version.department || '—',
    timestamp: formatAuditTimestamp(version.submitted_at),
    signatureMeaning: version.signature_meaning || (isInitial ? INITIAL_SIGNATURE_MEANING : EDITED_SIGNATURE_MEANING),
    signatureUniqueId: version.signature_unique_id || '—',
    certificateDetails: version.certificate_details || null,
    details: version.details || []
  }
}

function buildVersionHtml (version) {
  const meta = buildVersionMeta(version)
  const accentColor = meta.isInitial ? '#15803d' : '#0369a1'
  const badgeBg = meta.isInitial ? '#dcfce7' : '#e0f2fe'
  const badgeText = meta.isInitial ? '#166534' : '#075985'
  const signatureHtml = buildSignatureHtml(version)

  const metaLines = [
    ['Action', meta.action],
    [meta.actorLabel, meta.actorName],
    ['Role', meta.role],
    ['Department', meta.department],
    ['Date & Time', meta.timestamp],
    ['Meaning of Signature', meta.signatureMeaning]
  ].map(([label, value]) => `
    <div class="text-xs text-gray-700" style="line-height: 1.45; margin-bottom: 1px;">
      <span class="font-semibold">${escapeHtml(label)}:</span> ${escapeHtml(value || '—')}
    </div>
  `).join('')

  return `
    <div class="audit-history-version border border-blue-100 rounded-lg overflow-hidden shadow-sm mb-2" style="border-left: 4px solid ${accentColor}; background: #f8fafc;">
      <div class="flex items-center gap-2 px-3 py-2 bg-white border-b border-gray-200">
        <span class="inline-flex items-center justify-center px-2 py-0 rounded-full text-xs font-bold" style="background: ${badgeBg}; color: ${badgeText}; min-width: 36px;">
          ${escapeHtml(meta.versionLabel)}
        </span>
        <span class="text-sm font-bold text-gray-900">${escapeHtml(meta.title)}</span>
      </div>
      <div class="p-2">
        <div class="flex bg-white border border-gray-200 rounded overflow-hidden">
          <div style="width: 3px; background: ${accentColor}; flex-shrink: 0;"></div>
          <div class="py-2 px-3 flex-1">
            <div class="text-xs font-bold mb-1" style="color: ${accentColor};">Signed by:</div>
            <div class="mb-2">${signatureHtml}</div>
            ${metaLines}
            ${renderDetailsList(meta.details)}
            ${renderUniqueId(meta.signatureUniqueId, meta.certificateDetails)}
          </div>
        </div>
      </div>
    </div>
  `
}

function buildChangeSummaryHtml (summary) {
  const changes = summary.changes || []
  const completedBy = [summary.completed_by, summary.completed_by_role ? `(${summary.completed_by_role})` : null]
    .filter(Boolean)
    .join(' ')

  const rowsHtml = changes.map((change) => `
    <tr class="bg-white">
      <td class="border border-gray-300 px-2 py-1 text-sm whitespace-nowrap">${escapeHtml(change.field_name || change.field_key || '—')}</td>
      <td class="border border-gray-300 px-2 py-1 text-sm text-red-700 break-words">${escapeHtml(change.old_value || '—')}</td>
      <td class="border border-gray-300 px-2 py-1 text-sm text-green-700 break-words">${escapeHtml(change.new_value || '—')}</td>
      <td class="border border-gray-300 px-2 py-1 text-sm">${escapeHtml(change.changed_by || '—')}</td>
      <td class="border border-gray-300 px-2 py-1 text-sm whitespace-nowrap">${escapeHtml(formatAuditTimestamp(change.changed_on))}</td>
    </tr>
  `).join('')

  return `
    <div class="mb-3 px-3 py-2 rounded bg-white border border-gray-200 text-sm text-gray-700">
      <div><span class="font-semibold">Last Updated By:</span> ${escapeHtml(completedBy || '—')}</div>
      <div><span class="font-semibold">Last Updated On:</span> ${escapeHtml(formatAuditTimestamp(summary.completed_on))}</div>
      ${summary.summary_label ? `<div><span class="font-semibold">Event:</span> ${escapeHtml(summary.summary_label)}</div>` : ''}
    </div>
    <div class="overflow-x-auto">
      <table class="table-auto w-full border border-gray-300 text-sm">
        <thead class="bg-gray-100">
          <tr>
            <th class="text-left border border-gray-300 px-2 py-1 font-semibold">Field</th>
            <th class="text-left text-red-700 border border-gray-300 px-2 py-1 font-semibold">Previous Value</th>
            <th class="text-left text-green-700 border border-gray-300 px-2 py-1 font-semibold">New Value</th>
            <th class="text-left border border-gray-300 px-2 py-1 font-semibold">Changed By</th>
            <th class="text-left border border-gray-300 px-2 py-1 font-semibold">Changed On</th>
          </tr>
        </thead>
        <tbody>
          ${rowsHtml}
        </tbody>
      </table>
    </div>
  `
}

export default class DmRequestAuditHistoryModule {
  constructor ({ auditHistoryUrl, auditHistoryTarget, changeSummaryTarget = null }) {
    this.auditHistoryUrl = auditHistoryUrl
    this.auditHistoryTarget = auditHistoryTarget
    this.changeSummaryTarget = changeSummaryTarget
    this.historyData = null
  }

  async load () {
    if (!this.auditHistoryUrl || !this.auditHistoryTarget) return

    try {
      const response = await fetch(this.auditHistoryUrl, {
        headers: { Accept: 'application/json' }
      })

      if (!response.ok) {
        this.showMessage('Unable to load audit history.')
        this.hideChangeSummary()
        return
      }

      this.historyData = await response.json()
      this.renderPanel()
      this.renderChangeSummary()
      this.attachCertificateToggles()
    } catch (error) {
      console.error('Failed to load DM request audit history:', error)
      this.showMessage('Unable to load audit history.')
      this.hideChangeSummary()
    }
  }

  hideChangeSummary () {
    if (!this.changeSummaryTarget) return
    this.changeSummaryTarget.classList.add('hidden')
    this.changeSummaryTarget.innerHTML = ''
  }

  showMessage (message) {
    this.auditHistoryTarget.innerHTML = `<p class="text-sm text-gray-500 italic">${escapeHtml(message)}</p>`
  }

  renderPanel () {
    const versions = this.historyData?.versions || []
    const message = this.historyData?.message

    if (!versions.length && !message) {
      this.auditHistoryTarget.innerHTML = '<p class="text-sm text-gray-500 italic mb-0">No audit history is available for this request yet.</p>'
      return
    }

    const versionsHtml = versions.map((version) => buildVersionHtml(version)).join('')
    const noticeHtml = message
      ? `<div class="mb-3 px-3 py-2 rounded text-sm" style="background: #fffbeb; border: 1px solid #fde68a; color: #92400e;">${escapeHtml(message)}</div>`
      : ''
    const emptyHtml = !versions.length
      ? '<p class="text-sm text-gray-500 italic mb-0">No audit history is available for this request yet.</p>'
      : ''

    this.auditHistoryTarget.innerHTML = `
      ${noticeHtml}
      <div>${versionsHtml || emptyHtml}</div>
    `
  }

  renderChangeSummary () {
    if (!this.changeSummaryTarget) return

    const summary = this.historyData?.change_summary
    const changes = summary?.changes || []

    if (!summary?.has_changes || !changes.length) {
      this.hideChangeSummary()
      return
    }

    this.changeSummaryTarget.classList.remove('hidden')
    this.changeSummaryTarget.innerHTML = buildChangeSummaryHtml(summary)
  }

  attachCertificateToggles () {
    this.auditHistoryTarget.querySelectorAll('.audit-history-unique-id-toggle').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault()
        const panel = button.parentElement?.querySelector('.audit-history-certificate-panel')
        if (!panel) return

        const isHidden = panel.classList.contains('hidden')
        panel.classList.toggle('hidden', !isHidden)
        button.setAttribute('aria-expanded', isHidden ? 'true' : 'false')
        button.title = isHidden ? 'Hide signature certificate details' : 'Show signature certificate details'
      })
    })
  }
}

export { buildAuditHistoryJsonUrl }
